import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute, Router, RouterStateSnapshot } from '@angular/router';
import { Observable, Subscription } from 'rxjs';

import { TranslateService } from '@ngx-translate/core';
import Keyboard from 'simple-keyboard';

import { IProcessOrder } from 'src/app/shared/interfaces/IProcessOrder';

import { ApiErrorHandlerService } from '../../services/api-error-handler/api-error-handler.service';
import { TSettings } from '../../services/appconfig/settings.model';
import { OrderProcessingService } from '../../services/order-processing/order-processing.service';
import { SettingsService } from '../../services/settings/settings.service';
import { Utils } from '../../utils/utils';

import {
  ALPHA_LAYOUT_DEFAULT,
  ALPHA_LAYOUT_DISPLAY,
  ALPHA_LAYOUT_SHIFT,
  CODE_ALPHANUMERIC_REGEX,
  COMMON_PATHS,
  KEY_ALPHANUMERIC_REGEX,
  MOBILE_TYPE,
  ORDER_ID_TYPE,
  RETAIL_PATHS,
  SCAN_TYPE,
  SIMPLE_KEYBOARD_SPECIAL_KEY_REGEX,
  TOAST_TYPES,
  VIEW_TYPES,
} from './../../utils/constants';

@Component({
  selector: 'frodo-alpha-keyboard',
  templateUrl: './alpha-keyboard.component.html',
  styleUrls: ['./alpha-keyboard.component.scss'],
})
export class AlphaKeyboardComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Input() viewType;
  @Input() side;
  @Input() globalIsLoading = false;
  @Input() disableEnter = false;
  @Input() preventSubmit?: () => boolean;
  @Output() deliveryListSearch: EventEmitter<void> = new EventEmitter();
  @Output() valueSubmit: EventEmitter<string> = new EventEmitter();

  code: string;
  flow = '';
  isLoading = false;
  label = '';
  type = '';
  state: RouterStateSnapshot;
  allowAlphanumeric: boolean;
  expireLoaded: boolean;
  keyboard: Keyboard;
  subscription: Subscription = new Subscription();
  hdSettings$: Observable<Partial<TSettings>>;
  minLength = 0;
  maxLength = 0;
  allowSymbols: boolean;
  currentBoxInput: string;
  showBoxes = false;
  inputs = {};
  isDeliveryList = false;
  enableDeliveryProvider = false;
  keydownHandler: (event: KeyboardEvent) => void;
  layouts = {
    default: ALPHA_LAYOUT_DEFAULT,
    shift: ALPHA_LAYOUT_SHIFT,
  };
  disabledButtonsByLayout = {
    default: '',
    shift: '',
  };
  allowedSpecialKeys = new Set(['{shift}', '{lock}', '{bksp}', '{enter}']);

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private settingsService: SettingsService,
    private utils: Utils,
    private apiErrorHandlerService: ApiErrorHandlerService,
    private translateService: TranslateService,
    private orderProcessingService: OrderProcessingService
  ) {
    this.state = this.router.routerState.snapshot;
    this.hdSettings$ = this.settingsService.getSettings;
  }

  ngOnInit(): void {
    localStorage.removeItem('scheduleId');
    localStorage.removeItem('dispenseId');
    localStorage.removeItem('schedule');
    this.type = this.activatedRoute.snapshot.paramMap.get('type');
    this.flow = this.activatedRoute.snapshot.paramMap.get('flow');
    this.isDeliveryList = this.state.url.includes('delivery-list');
    this.subscription.add(
      this.hdSettings$.subscribe((response) => {
        this.allowAlphanumeric = response['accessCode']['allowAlphanumeric'];
        this.expireLoaded = response['defaultSettings']['expireLoaded'];
        this.minLength = response.accessCode.length.minimum;
        this.maxLength = response.accessCode.length.maximum;
        this.allowSymbols = response.accessCode.allowSymbols;
        this.showBoxes = this.minLength === this.maxLength;
        this.enableDeliveryProvider = response.apps.frodo.enableDeliveryProvider;

        this.cleanKeyboardInstance();
        this.prepareKeyboardRestrictions();
        this.initKeyboardInstance();

        if (!this.keydownHandler) this.addPhysicalKeyboardListener();
      })
    );
    this.subscription.add(
      this.utils.getLoadingValue().subscribe((value) => {
        this.isLoading = value;
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disableEnter'] && !changes['disableEnter'].firstChange && this.keyboard) {
      this.applyKeyRestrictions();
    }
  }

  ngAfterViewInit(): void {
    this.initInputBoxesValues();
  }

  initKeyboardInstance(): void {
    this.keyboard = new Keyboard({
      inputName: this.currentBoxInput,
      preventMouseDownDefault: true,
      syncInstanceInputs: true,
      disableButtonHold: true,
      onKeyPress: (button) => this.onKeyPress(button),
      layout: {
        default: ALPHA_LAYOUT_DEFAULT,
        shift: ALPHA_LAYOUT_SHIFT,
      },
      display: ALPHA_LAYOUT_DISPLAY,
    });

    this.applyKeyRestrictions();
  }

  addPhysicalKeyboardListener(): void {
    this.keydownHandler = (event: KeyboardEvent) => {
      event.preventDefault();
      const key = event.key;

      switch (key) {
        case 'Shift':
        case 'CapsLock':
          break;
        case 'Backspace':
          this.delete();
          break;
        case 'Enter':
          if (!this.globalIsLoading) this.confirm();
          break;
        case 'Tab':
          this.onTabKeyPress();
          break;
        default:
          if (this.shouldSkipKeyPress(key)) return;
          this.setValueFromExternalKeyboard(event);
          break;
      }
    };
    document.addEventListener('keydown', this.keydownHandler);
  }

  setValueFromExternalKeyboard(event: KeyboardEvent): void {
    this.currentBoxInput = event.target['id'];
    this.onChange(event.key);
  }

  onInputFocus(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.currentBoxInput = inputElement.id;
    this.keyboard.setOptions({
      inputName: this.currentBoxInput,
    });
  }

  onChange(value: string): void {
    const selector = document.getElementById('code');
    const currentCode = selector ? selector['value'] : this.getInputBoxesValues();

    if (this.isDeliveryList) {
      return this.setValue(value);
    }
    if (selector && selector['value'].length >= this.maxLength) {
      return this.showLengthRestrictionInputMsg();
    }
    if (this.getInputBoxesValues().length >= this.maxLength) {
      return this.showLengthRestrictionBoxesMsg();
    }
    if (currentCode.length < this.maxLength) {
      return this.setValue(value);
    }
  }

  onKeyPress(key: string): void {
    if (this.shouldSkipKeyPress(key)) return;

    switch (key) {
      case '{shift}':
      case '{lock}':
        this.handleShift();
        break;
      case '{bksp}':
        this.delete();
        break;
      case '{enter}':
        if (!this.globalIsLoading) this.confirm();
        break;
      case '{tab}': {
        this.onTabKeyPress();
        break;
      }
      case '{space}':
        this.onChange(' ');
        break;
      default:
        this.onChange(key);
        break;
    }
  }

  handleShift(): void {
    const currentLayout = this.keyboard.options.layoutName;
    const shiftToggle = currentLayout === 'default' ? 'shift' : 'default';

    this.keyboard.setOptions({
      layoutName: shiftToggle,
    });

    this.applyKeyRestrictions();
  }

  focusNext(index: number): void {
    const currentInput = document.getElementById(this.currentBoxInput);
    const currentValue = currentInput['value'];
    const nextId = index + 1;
    if (currentValue && nextId < this.maxLength) {
      this.setFocus(`input${nextId}`);
    }
  }

  getInputIndex(): number {
    return this.currentBoxInput.length > 6
      ? parseInt(this.currentBoxInput.slice(-2))
      : parseInt(this.currentBoxInput.slice(-1));
  }

  getInputBoxesValues(): string {
    const values = Object.values(this.inputs);
    return values.join('');
  }

  initInputBoxesValues(): void {
    let initialId = 'input0';
    const boxInput = document.getElementById(initialId);
    if (boxInput) {
      for (let index = 0; index < this.maxLength; index++) {
        const inputId = `input${index}`;
        this.inputs[inputId] = '';
        document.getElementById(inputId)['value'] = '';
        this.keyboard.clearInput(inputId);
      }
      this.setFocus(initialId);
    } else if (!this.isDeliveryList) {
      initialId = 'code';
      document.getElementById(initialId)['value'] = '';
      this.keyboard.clearInput(initialId);
      this.setFocus(initialId);
    }
  }

  showLengthRestrictionInputMsg(): void {
    const msg = this.translateService.instant('common.codeLengthRestrictionInputAlphaMsg', {
      min: this.minLength,
      max: this.maxLength,
    });
    this.apiErrorHandlerService.showMessage(msg, TOAST_TYPES.Error);
  }

  showLengthRestrictionBoxesMsg(): void {
    const msg = this.translateService.instant('common.codeLengthRestrictionBoxesAlphaMsg', {
      max: this.maxLength,
    });
    this.apiErrorHandlerService.showMessage(msg, TOAST_TYPES.Error);
  }

  showAllowSymbolsRestrictionMsg(): void {
    const msg = this.translateService.instant('common.codeAllowSymbolsRestrictionMsg', {
      code: this.code,
    });
    this.apiErrorHandlerService.showMessage(msg, TOAST_TYPES.Error);
  }

  setValue(value: string): void {
    const selector = document.getElementById('code');
    const boxInput: HTMLElement = document.getElementById(this.currentBoxInput);
    if (selector) {
      const currentValue = selector['value'];
      const newValue = currentValue ? `${currentValue}${value}` : value;
      selector['value'] = newValue;
    } else if (boxInput) {
      this.setBoxesValues(boxInput, value);
      this.focusNext(this.getInputIndex());
    }
    this.keyboard.setInput(value, this.currentBoxInput);
  }

  setBoxesValues(boxInput: HTMLElement, value: string): void {
    if (value && boxInput['value'].length > 0) return;
    boxInput['value'] = value;
    this.inputs[this.currentBoxInput] = value;
  }

  setFocus(id: string): void {
    this.currentBoxInput = id;
    document.getElementById(id).focus();
  }

  delete(): void {
    const selector = document.getElementById('code');
    if (!selector) {
      this.removeBoxesValues();
    } else {
      const currentValue = selector['value'];
      const newValue = currentValue.slice(0, -1);
      selector['value'] = newValue;
    }
  }

  removeBoxesValues(): void {
    const currentInput = document.getElementById(this.currentBoxInput);
    const currentValue = currentInput['value'];
    const index = this.getInputIndex();
    if (currentValue) {
      const newValue = currentValue.slice(0, -1);
      this.setFocus(this.currentBoxInput);
      this.setValue(newValue);
    } else {
      const prevId = index - 1;
      const prevInput = `input${prevId}`;
      const prevBoxInput = document.getElementById(prevInput);
      if (prevBoxInput) {
        const prevValue = prevBoxInput['value'];
        const newValue = prevValue.slice(0, -1);
        this.setFocus(prevInput);
        this.setValue(newValue);
      }
    }
  }

  private isInputLengthValid(): boolean {
    return this.code.length >= this.minLength && this.code.length <= this.maxLength;
  }

  private isBoxesLengthValid(): boolean {
    return this.code.length === this.maxLength;
  }

  preventConfirm(): boolean {
    const input = document.getElementById('code');
    const boxInput = document.getElementById('input0');
    this.code = input ? input['value'] : this.getInputBoxesValues();

    if (!this.allowSymbols && !CODE_ALPHANUMERIC_REGEX.test(this.code)) {
      this.showAllowSymbolsRestrictionMsg();
      return true;
    }

    const shouldValidateCodeLength = [ORDER_ID_TYPE, SCAN_TYPE].includes(this.type);

    if (shouldValidateCodeLength) {
      if (input && !this.isInputLengthValid()) {
        this.showLengthRestrictionInputMsg();
        return true;
      }

      if (boxInput && !this.isBoxesLengthValid()) {
        this.showLengthRestrictionBoxesMsg();
        return true;
      }
    }

    return false;
  }

  confirm(): void {
    if (this.globalIsLoading || this.disableEnter) return;
    if (this.preventSubmit?.()) return;
    if (this.isDeliveryList) {
      this.deliveryListSearch.emit();
      return;
    }
    if (this.preventConfirm()) return;
    this.initInputBoxesValues();
    if (isNaN(Number(this.code))) this.code = this.code.toLowerCase();
    if (this.valueSubmit.observed) {
      this.valueSubmit.emit(this.code);
      return;
    }
    const isCoreType = [ORDER_ID_TYPE, MOBILE_TYPE].includes(this.type);
    const orderToProcess = this.buildProcessOrder();
    this.orderProcessingService.orderToProcess = orderToProcess;
    if (this.enableDeliveryProvider && isCoreType) {
      this.utils.redirect(`/${this.viewType}/${COMMON_PATHS.DELIVERY_PROVIDER}`);
    } else {
      this.orderProcessingService.processOrder(orderToProcess);
    }
  }

  private buildProcessOrder(): IProcessOrder {
    return {
      code: this.code,
      type: this.type,
      flow: this.flow,
      side: this.side,
      expireLoaded: this.expireLoaded,
      frodoContext: this.viewType,
      initTransactionByCode: this.isRetailInitTransactionByCode(),
    };
  }

  private isRetailInitTransactionByCode(): boolean {
    return (
      this.viewType === VIEW_TYPES.RETAIL && this.state.url.includes(RETAIL_PATHS.ENTER_SCAN_CODE)
    );
  }

  ngOnDestroy(): void {
    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler);
      this.keydownHandler = undefined;
    }

    this.cleanKeyboardInstance();

    this.subscription.unsubscribe();
  }

  cleanKeyboardInstance(): void {
    if (this.keyboard) {
      try {
        this.keyboard.destroy();
      } catch (error) {
        /* istanbul ignore next */
        console.warn('Error destroying keyboard:', error);
      }
      this.keyboard = undefined;
    }
  }

  onTabKeyPress(): void {
    if (!this.allowSymbols) return;
    const boxInput = document.getElementById('input0');
    if (boxInput) {
      const nextId = this.getInputIndex() + 1;
      if (nextId < this.maxLength) this.setFocus(`input${nextId}`);
    }
  }

  shouldSkipKeyPress(key: string): boolean {
    return !this.allowSymbols && this.isBlockedKey(key);
  }

  private getDisabledButtonsFromLayout(layoutRows: string[]): string {
    const visibleButtons = layoutRows.flatMap((row) => row.split(' '));
    return visibleButtons.filter((btn) => this.isBlockedKey(btn)).join(' ');
  }

  private prepareKeyboardRestrictions(): void {
    this.disabledButtonsByLayout.default = this.getDisabledButtonsFromLayout(this.layouts.default);
    this.disabledButtonsByLayout.shift = this.getDisabledButtonsFromLayout(this.layouts.shift);
  }

  private applyKeyRestrictions(): void {
    const layoutName = this.keyboard.options.layoutName;
    const disabledButtons = this.allowSymbols ? '' : this.disabledButtonsByLayout[layoutName];
    const allDisabledButtons = [disabledButtons, this.disableEnter ? '{enter}' : '']
      .filter(Boolean)
      .join(' ');

    this.keyboard.setOptions({
      buttonTheme: allDisabledButtons
        ? [{ class: 'disabled-key', buttons: allDisabledButtons }]
        : [],
    });
  }

  private isBlockedKey(key: string): boolean {
    if (SIMPLE_KEYBOARD_SPECIAL_KEY_REGEX.test(key)) {
      return !this.allowedSpecialKeys.has(key);
    }

    return !KEY_ALPHANUMERIC_REGEX.test(key);
  }
}
